import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../components/ui/collapsible';
import { Loader2, CheckCircle, AlertCircle, ChevronDown, ChevronRight, FileText, Download } from 'lucide-react';

const ProfilePage = () => {
  const { user, updateProfile, isLoading } = useAuth();
  
  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    telephone: user?.telephone || '',
    country: user?.country || '',
  });
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [certificateOpen, setCertificateOpen] = useState(false);

  const handleChange = (e) => {
    setSaveSuccess(false);
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSaveSuccess(false);

    try {
      await updateProfile(formData);
      setSaveSuccess(true);
    } catch (err) {
      setError('Failed to update profile. Please try again.');
    }
  };

  const getDuesStatusColor = (status) => {
    switch (status) {
      case 'Yes':
        return 'bg-emerald-100 text-emerald-700';
      case 'No':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="p-6 md:p-12" data-testid="profile-page">
      <div className="max-w-2xl">
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-2">
          Profile
        </h1>
        <p className="text-gray-600 mb-8">
          View and update your profile information.
        </p>

        {/* Profile Form */}
        <Card className="border-gray-100 mb-6" data-testid="profile-form-card">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Personal Information</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <Alert variant="destructive" data-testid="profile-error">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {saveSuccess && (
                <Alert className="border-emerald-200 bg-emerald-50" data-testid="profile-success">
                  <CheckCircle className="h-4 w-4 text-emerald-600" />
                  <AlertDescription className="text-emerald-700">
                    Profile updated successfully.
                  </AlertDescription>
                </Alert>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name</Label>
                  <Input
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    data-testid="profile-firstname-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    data-testid="profile-lastname-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="telephone">Telephone Number</Label>
                <Input
                  id="telephone"
                  name="telephone"
                  type="tel"
                  value={formData.telephone}
                  onChange={handleChange}
                  placeholder="+44 131 555 0123"
                  data-testid="profile-telephone-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="country">Country</Label>
                <Input
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  placeholder="Scotland"
                  data-testid="profile-country-input"
                />
              </div>

              {/* Email (read-only) */}
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  value={user?.email}
                  disabled
                  className="bg-gray-100"
                  data-testid="profile-email-input"
                />
                <p className="text-xs text-gray-500">Email cannot be changed. Contact an administrator if needed.</p>
              </div>

              <Button
                type="submit"
                className="bg-emerald-600 hover:bg-emerald-700"
                disabled={isLoading}
                data-testid="profile-save-btn"
              >
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save Changes
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Membership Dues Status */}
        <Card className="border-gray-100 mb-6" data-testid="dues-status-card">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Membership Dues</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600">Current dues status:</p>
              </div>
              <span
                className={`px-3 py-1 rounded-full font-medium ${getDuesStatusColor(user?.duesStatus)}`}
                data-testid="dues-status-badge"
              >
                {user?.duesStatus || 'Unknown'}
              </span>
            </div>
            <p className="text-sm text-gray-500 mt-4">
              To update your dues status or make a payment, please visit WebCollect from the Members Dashboard.
            </p>
          </CardContent>
        </Card>

        {/* Certificate Section */}
        <Card className="border-gray-100" data-testid="certificate-card">
          <Collapsible open={certificateOpen} onOpenChange={setCertificateOpen}>
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-gray-50 transition-colors">
                <CardTitle className="font-heading text-lg flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-emerald-600" />
                    Certificate
                  </span>
                  {certificateOpen ? (
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                  )}
                </CardTitle>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="pt-0">
                {user?.certificateUrl ? (
                  <div className="space-y-4" data-testid="certificate-available">
                    <div className="aspect-[3/4] max-w-sm mx-auto bg-gray-100 rounded-lg flex items-center justify-center">
                      {/* PDF viewer would go here */}
                      <p className="text-gray-500">Certificate Preview</p>
                    </div>
                    <div className="text-center">
                      <Button
                        variant="outline"
                        className="gap-2"
                        onClick={() => window.open(user.certificateUrl, '_blank')}
                        data-testid="download-certificate-btn"
                      >
                        <Download className="h-4 w-4" />
                        Download Certificate
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8" data-testid="no-certificate">
                    <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No certificate available.</p>
                    <p className="text-sm text-gray-400 mt-2">
                      Contact the lodge administration if you believe this is an error.
                    </p>
                  </div>
                )}
              </CardContent>
            </CollapsibleContent>
          </Collapsible>
        </Card>
      </div>
    </div>
  );
};

export default ProfilePage;
